/**
 * /dreamweaver/embed · MC iframe-embeddable variant.
 *
 * Rendered when Aurelion / MindEscapes embed our Dreamweaver whisper
 * surface inside their concierge dashboard. Same doctrine as the
 * standalone page — sonnet_concepts stay off the wire, private_journal
 * never leaves the desk — but the chrome is stripped so the iframe
 * inherits the host site's frame.
 *
 * Query params supported (all optional; a corp session cookie wins):
 *   ?member_email=&member_name=&member_phone=&tenant=aurelion
 *
 * NOTE: The tenant sniffing already lives on the backend
 * (`_resolve_member_and_tenant`) — this page just posts the same
 * whisper body with `withCredentials: true`.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import MemberContextBand from "../components/MemberContextBand";
import CldImg from "../components/CldImg";
import { resizeImagesForUpload } from "@/lib/imageResize";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const T = {
  ink:      "#1a1a1a",
  mist:     "#f7f4ee",
  cream:    "#fbf9f4",
  brass:    "#a97e2d",
  brassSoft:"#c9a663",
  border:   "rgba(26,26,26,0.10)",
  muted:    "rgba(26,26,26,0.55)",
};

function useQuery() {
  const params = new URLSearchParams(window.location.search);
  return Object.fromEntries(params.entries());
}

export default function DreamweaverEmbedPage() {
  const q = useQuery();

  const [text, setText]           = useState("");
  const [urgency, setUrgency]     = useState("someday");
  const [visibility, setVis]      = useState("shared");
  const [consentStore, setCS]     = useState(false);
  const [consentPhotos, setCPh]   = useState(false);
  const [consentVoice, setCV]     = useState(false);
  const [submitting, setSub]      = useState(false);
  const [error, setError]         = useState("");
  const [saved, setSaved]         = useState(null);

  const [photos, setPhotos]       = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);

  const [voiceUrl, setVoiceUrl]   = useState("");
  const [voiceBusy, setVB]        = useState(false);
  const [voiceRec, setVR]         = useState(false);
  const mediaRef                  = React.useRef(null);
  const chunksRef                 = React.useRef([]);

  // Seed inspirations · same 12 the main page shows. Tapping one
  // pre-fills the whisper textarea; the member can edit before submit.
  const [inspirations, setInspirations] = useState([]);

  useEffect(() => {
    document.body.style.background = "transparent";
    (async () => {
      try {
        const r = await axios.get(`${API}/api/dreamweaver/inspirations`);
        setInspirations(r.data?.inspirations || []);
      } catch (_) { /* silent · endpoint is public but harmless if it fails */ }
    })();
    return () => { document.body.style.background = ""; };
  }, []);

  const inspirationsGrouped = React.useMemo(() => {
    const out = { close: [], medium: [], far: [], legacy: [] };
    inspirations.forEach((i) => { if (out[i.register]) out[i.register].push(i); });
    return out;
  }, [inspirations]);

  async function submit(e) {
    e?.preventDefault?.();
    setError("");
    if (!consentStore)         return setError("Please tick the box below before whispering.");
    if (text.trim().length < 3) return setError("A dream needs a few words to travel.");
    setSub(true);
    try {
      const body = {
        dream_text: text.trim(),
        urgency,
        member_email: q.member_email || undefined,
        member_name:  q.member_name  || undefined,
        member_phone: q.member_phone || undefined,
        consent: {
          store_process: { given: true },
          share_choice:  visibility,
          ...(consentPhotos && photos.length > 0 ? { photos: { given: true } } : {}),
          ...(consentVoice  && voiceUrl         ? { voice_transcription: { given: true } } : {}),
        },
        ...(photos.length > 0 ? { mood_photo_urls: photos.map((p) => p.url) } : {}),
        ...(voiceUrl          ? { voice_url:       voiceUrl } : {}),
      };
      const r = await axios.post(`${API}/api/dreamweaver/whisper`, body, { withCredentials: true });
      setSaved(r.data);
      setText(""); setPhotos([]); setVoiceUrl("");
      setCPh(false); setCV(false); setCS(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSub(false);
    }
  }

  async function onPhotoPick(e) {
    const rawFiles = Array.from(e.target.files || []);
    if (!rawFiles.length) return;
    if (photos.length + rawFiles.length > 3) {
      setError("Up to three photos per dream.");
      e.target.value = "";
      return;
    }
    setPhotoBusy(true); setError("");
    try {
      // v3.48 · client-side resize (see /pages/Dreamweaver.jsx for rationale)
      const files = await resizeImagesForUpload(rawFiles);
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(`${API}/api/dreamweaver/upload/photo`, fd, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ url: r.data.secure_url, public_id: r.data.public_id });
      }
      setPhotos((p) => [...p, ...uploaded]);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setPhotoBusy(false); e.target.value = "";
    }
  }

  async function startRecording() {
    setError("");
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError("Voice isn't available in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (evt) => { if (evt.data && evt.data.size) chunksRef.current.push(evt.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (blob.size < 400) { setError("Recording too short."); return; }
        setVB(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice-" + Date.now() + ".webm");
          fd.append("consent_voice", "true");
          const r = await axios.post(`${API}/api/dreamweaver/transcribe`, fd, {
            withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
          });
          setVoiceUrl(r.data.voice_url || "");
          if (r.data.transcript && r.data.transcript.trim()) {
            setText((cur) => cur.trim() ? cur.trim() + "\n\n" + r.data.transcript : r.data.transcript);
          }
        } catch (err) {
          setError(err?.response?.data?.detail || err.message);
        } finally { setVB(false); }
      };
      mediaRef.current = rec;
      rec.start(); setVR(true);
      setTimeout(() => { try { if (rec.state === "recording") rec.stop(); } catch(_) { /* noop */ } }, 180000);
    } catch (_) {
      setError("Microphone permission denied.");
    }
  }
  function stopRecording() {
    try { mediaRef.current?.stop(); } catch (_) { /* noop */ }
    setVR(false);
  }

  return (
    <div data-testid="dreamweaver-embed"
      style={{
        minHeight: "100vh", background: T.cream, color: T.ink,
        fontFamily: '"EB Garamond", Georgia, serif', padding: "24px 20px",
      }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
                      color: T.brass, marginBottom: 8 }}>
          Dreamweaver · powered by Les Concierges
        </div>
        <h1 style={{ fontSize: 28, lineHeight: 1.15, margin: 0, fontWeight: 400,
                     letterSpacing: "-0.01em" }}>
          What have you <em style={{ color: T.brass, fontStyle: "italic" }}>always</em> wanted?
        </h1>
        <p style={{ marginTop: 10, fontSize: 15, lineHeight: 1.5, color: T.muted }}>
          Whisper it here. A Concierge® holds it — and, when the moment arrives, weaves it into being.
        </p>

        <MemberContextBand pillar="dreamweaver" style={{ marginTop: 22 }} />

        {/* Inspirations shelf · same 12 seed prompts as the main page.
            Tapping one pre-fills the whisper textarea (member edits
            before submit). Grouped register-first (close → medium →
            far → legacy) so the softer, quieter ones sit lower. */}
        {inspirations.length > 0 && !saved && (
          <section data-testid="embed-inspirations"
                   style={{ marginTop: 26, marginBottom: 8 }}>
            <div style={{
              fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase",
              color: T.muted, marginBottom: 10,
            }}>
              To begin · a few inspirations
            </div>
            <div style={{ display: "grid", gap: 8,
                          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
              {["close","medium","far","legacy"].flatMap((r) =>
                (inspirationsGrouped[r] || []).map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    data-testid={`embed-insp-${i.id}`}
                    onClick={() => setText(i.text)}
                    style={{
                      textAlign: "left", cursor: "pointer",
                      background: "white", border: `1px solid ${T.border}`,
                      borderRadius: 8, padding: "11px 13px",
                      fontFamily: "inherit", color: T.ink,
                      fontSize: 13, lineHeight: 1.45,
                      transition: "border-color .15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brassSoft; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; }}
                  >
                    <div style={{
                      fontSize: 9, textTransform: "uppercase",
                      letterSpacing: "0.2em", color: T.brass, marginBottom: 5,
                    }}>
                      {i.register === "legacy" ? "· quiet" :
                       i.register === "far"    ? "· someday" :
                       i.register === "medium" ? "· this year" : "· soon"}
                    </div>
                    <div>{i.text}</div>
                  </button>
                ))
              )}
            </div>
          </section>
        )}

        {saved ? (
          <div data-testid="embed-saved"
            style={{ marginTop: 24, padding: 20, background: "white",
                     border: `1px solid ${T.brassSoft}`, borderRadius: 12 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase",
                          color: T.brass, marginBottom: 6 }}>Whispered</div>
            <div style={{ fontSize: 16, lineHeight: 1.5 }}>{saved.dream_text}</div>
            <div style={{ marginTop: 14, fontSize: 13, color: T.muted, fontStyle: "italic" }}>
              A Concierge® will hold this quietly · you'll hear from us when the moment finds you.
            </div>
            <button
              onClick={() => setSaved(null)}
              data-testid="embed-whisper-another"
              style={{
                marginTop: 12, padding: "6px 14px", borderRadius: 999,
                border: `1px solid ${T.border}`, background: "transparent",
                fontFamily: "inherit", fontSize: 12, color: T.muted, cursor: "pointer",
              }}>
              Whisper another
            </button>
          </div>
        ) : (
          <form onSubmit={submit}
                style={{ marginTop: 20, background: "white", border: `1px solid ${T.border}`,
                         borderRadius: 12, padding: 18 }}>
            <textarea
              data-testid="embed-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Say it in your own words."
              rows={5}
              style={{
                width: "100%", border: 0, outline: 0, resize: "vertical",
                fontSize: 16, lineHeight: 1.55, fontFamily: "inherit",
                color: T.ink, background: "transparent",
              }} />

            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {[{k:"someday",l:"Someday"},{k:"this_year",l:"This year"},{k:"right_now",l:"Right now"}].map((u) => (
                <button key={u.k} type="button"
                  data-testid={`embed-urgency-${u.k}`}
                  onClick={() => setUrgency(u.k)}
                  style={{
                    padding: "5px 12px", borderRadius: 999,
                    border: `1px solid ${urgency === u.k ? T.brass : T.border}`,
                    background: urgency === u.k ? T.brass : "transparent",
                    color: urgency === u.k ? "white" : T.ink,
                    fontFamily: "inherit", fontSize: 12, cursor: "pointer",
                  }}>{u.l}</button>
              ))}
            </div>

            {/* Voice */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 14, fontSize: 12,
                            color: T.muted, cursor: "pointer" }}>
              <input type="checkbox" data-testid="embed-consent-voice"
                     checked={consentVoice} onChange={(e) => setCV(e.target.checked)}
                     style={{ marginTop: 3 }} />
              <span>I'd like to whisper aloud (transcribed securely)</span>
            </label>
            <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {!voiceRec ? (
                <button type="button" onClick={startRecording}
                  data-testid="embed-voice-record"
                  disabled={!consentVoice || voiceBusy || !!voiceUrl}
                  style={{
                    padding: "5px 12px", borderRadius: 999, border: `1px solid ${T.brass}`,
                    background: "white", color: T.brass, fontFamily: "inherit", fontSize: 12,
                    cursor: consentVoice ? "pointer" : "not-allowed",
                    opacity: consentVoice ? 1 : 0.5,
                  }}>
                  {voiceUrl ? "Attached" : voiceBusy ? "Transcribing…" : "● Record"}
                </button>
              ) : (
                <button type="button" onClick={stopRecording}
                  data-testid="embed-voice-stop"
                  style={{
                    padding: "5px 12px", borderRadius: 999, border: `1px solid ${T.brass}`,
                    background: T.brass, color: "white", fontFamily: "inherit", fontSize: 12,
                    cursor: "pointer",
                  }}>■ Stop</button>
              )}
              {voiceUrl && <audio controls src={voiceUrl} style={{ height: 28, maxWidth: 240 }} />}
            </div>

            {/* Photos */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 14, fontSize: 12,
                            color: T.muted, cursor: "pointer" }}>
              <input type="checkbox" data-testid="embed-consent-photos"
                     checked={consentPhotos} onChange={(e) => setCPh(e.target.checked)}
                     style={{ marginTop: 3 }} />
              <span>Include up to three mood photos</span>
            </label>
            <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <input id="dw-embed-photo" type="file" accept="image/*" multiple
                     data-testid="embed-photo-input"
                     onChange={onPhotoPick}
                     disabled={!consentPhotos || photoBusy || photos.length >= 3}
                     style={{ display: "none" }} />
              <label htmlFor="dw-embed-photo"
                style={{
                  padding: "5px 12px", borderRadius: 999, border: `1px solid ${T.brass}`,
                  background: "white", color: T.brass, fontFamily: "inherit", fontSize: 12,
                  cursor: (!consentPhotos || photos.length >= 3) ? "not-allowed" : "pointer",
                  opacity: (!consentPhotos || photos.length >= 3) ? 0.5 : 1,
                }}>
                {photoBusy ? "Uploading…" : photos.length >= 3 ? "Three added" : "+ Photo"}
              </label>
              {photos.map((p, i) => (
                <div key={p.url} style={{ width: 48, height: 48, borderRadius: 6, overflow: "hidden",
                                          border: `1px solid ${T.border}` }}>
                  <CldImg src={p.url} w={96} alt="mood"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              ))}
            </div>

            {/* Visibility */}
            <fieldset style={{ marginTop: 16, border: 0, padding: 0 }}>
              <legend style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
                               color: T.muted, marginBottom: 6 }}>Who may hold this</legend>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8,
                              fontSize: 13, cursor: "pointer", marginBottom: 4 }}>
                <input type="radio" name="vis-emb" checked={visibility === "shared"}
                       data-testid="embed-vis-shared"
                       onChange={() => setVis("shared")} style={{ marginTop: 3 }} />
                <span>Share with my Concierge®</span>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8,
                              fontSize: 13, cursor: "pointer" }}>
                <input type="radio" name="vis-emb" checked={visibility === "private_journal"}
                       data-testid="embed-vis-private"
                       onChange={() => setVis("private_journal")} style={{ marginTop: 3 }} />
                <span>Keep this private for now</span>
              </label>
            </fieldset>

            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 14,
                            fontSize: 12, color: T.muted, cursor: "pointer" }}>
              <input type="checkbox" data-testid="embed-consent-store"
                     checked={consentStore} onChange={(e) => setCS(e.target.checked)}
                     style={{ marginTop: 3 }} />
              <span>
                I'm happy for Concierge® to hold this dream and work on it for me.{" "}
                <a href="/legal" target="_blank" rel="noopener noreferrer"
                   style={{ color: T.brass }}>Privacy</a>
              </span>
            </label>

            {error && (
              <div data-testid="embed-error"
                style={{ marginTop: 12, padding: "8px 12px", borderRadius: 6,
                         background: "#fdecea", color: "#8a2c1f", fontSize: 13 }}>{error}</div>
            )}

            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <button type="submit"
                data-testid="embed-submit"
                disabled={submitting || !consentStore || text.trim().length < 3}
                style={{
                  padding: "9px 22px", borderRadius: 999, border: `1px solid ${T.brass}`,
                  background: submitting ? T.brassSoft : T.brass, color: "white",
                  fontFamily: "inherit", fontSize: 14, cursor: submitting ? "wait" : "pointer",
                  opacity: (submitting || !consentStore) ? 0.6 : 1,
                }}>
                {submitting ? "Weaving…" : "Whisper this dream"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
